(() => {
  const doc = document;

  /* ——— Mobile header disclosure ——— */
  const header = doc.querySelector('[data-bnn-header]');

  if (header) {
    const toggle = header.querySelector('[data-bnn-nav-toggle]');
    const panel = header.querySelector('[data-bnn-nav-panel]');

    const setPanelOpen = (open) => {
      if (!toggle || !panel) return;
      toggle.setAttribute('aria-expanded', String(open));
      panel.classList.toggle('is-open', open);
      if (open) {
        panel.removeAttribute('hidden');
        if ('inert' in panel) panel.inert = false;
      } else {
        panel.setAttribute('hidden', '');
        if ('inert' in panel) panel.inert = true;
      }
    };

    const closeMobileNav = ({ restoreFocus = false } = {}) => {
      if (!toggle || !panel) return;
      const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
      setPanelOpen(false);
      if (restoreFocus && wasOpen) {
        toggle.focus();
      }
    };

    if (toggle && panel) {
      setPanelOpen(false);

      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        setPanelOpen(!open);
        if (!open) {
          const first = panel.querySelector('a, button');
          if (first) {
            first.focus();
          } else {
            toggle.focus();
          }
        }
      });

      panel.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => closeMobileNav({ restoreFocus: false }));
      });

      doc.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeMobileNav({ restoreFocus: true });
        }
      });
    }
  }

  /* ——— Poster-first click-to-play video ——— */
  const wireVideoRoot = (root) => {
    if (!root || root.dataset.bnnVideoWired === 'true') return;
    root.dataset.bnnVideoWired = 'true';

    const video = root.querySelector('[data-bnn-video-el]');
    const source = root.querySelector('[data-bnn-video-src]');
    const play = root.querySelector('[data-bnn-video-play]');
    const poster = root.querySelector('[data-bnn-video-poster]');
    const fallback = root.querySelector('[data-bnn-video-fallback]');
    if (!video || !source || !play) return;

    const markPlaying = () => {
      root.classList.add('is-playing');
      play.hidden = true;
      if (poster) poster.hidden = true;
      if (fallback) fallback.hidden = true;
    };

    const markStopped = () => {
      root.classList.remove('is-playing');
      play.hidden = false;
      if (poster) poster.hidden = false;
    };

    const ensureSource = () => {
      if (root.dataset.bnnVideoReady === 'true') return true;
      const src = source.getAttribute('data-src');
      if (!src) return false;
      source.setAttribute('src', src);
      video.load();
      root.dataset.bnnVideoReady = 'true';
      return true;
    };

    video.addEventListener('play', markPlaying);
    video.addEventListener('pause', () => {
      /* Mid-play pause keeps native controls; restore poster only at start/end */
      if (video.ended || video.currentTime < 0.05) markStopped();
    });
    video.addEventListener('ended', markStopped);
    video.addEventListener('error', () => {
      if (fallback) fallback.hidden = false;
      play.hidden = true;
      root.classList.remove('is-playing');
    });

    play.addEventListener('click', () => {
      if (!ensureSource()) {
        if (fallback) fallback.hidden = false;
        return;
      }
      video
        .play()
        .then(markPlaying)
        .catch(() => {
          if (fallback) fallback.hidden = false;
          play.hidden = true;
        });
    });
  };

  const pauseVideoRoot = (root) => {
    if (!root) return;
    const video = root.querySelector('[data-bnn-video-el]');
    const play = root.querySelector('[data-bnn-video-play]');
    const poster = root.querySelector('[data-bnn-video-poster]');
    if (!video) return;
    try {
      video.pause();
      video.currentTime = 0;
    } catch (_err) {
      /* ignore */
    }
    root.classList.remove('is-playing');
    if (play) play.hidden = false;
    if (poster) poster.hidden = false;
  };

  doc.querySelectorAll('[data-bnn-video]').forEach(wireVideoRoot);

  /* ——— Single-player walkthrough gallery ——— */
  const resetWalkthrough = (gallery) => {
    if (!gallery) return;
    const video = gallery.querySelector('[data-bnn-walkthrough-video]');
    const source = gallery.querySelector('[data-bnn-walkthrough-source]');
    const play = gallery.querySelector('[data-bnn-walkthrough-play]');
    const poster = gallery.querySelector('[data-bnn-walkthrough-poster]');
    const fallback = gallery.querySelector('[data-bnn-walkthrough-fallback]');
    const player = gallery.querySelector('[data-bnn-walkthrough-player]');
    if (!video) return;

    try {
      video.pause();
      video.currentTime = 0;
    } catch (_err) {
      /* ignore */
    }

    if (source) source.removeAttribute('src');
    try {
      video.removeAttribute('src');
      video.load();
    } catch (_err) {
      /* ignore */
    }

    gallery.dataset.bnnWalkthroughReady = 'false';
    if (player) player.classList.remove('is-playing');
    if (play) play.hidden = false;
    if (poster) poster.hidden = false;
    if (fallback) fallback.hidden = true;
  };

  /* Pause/reset inactive project videos when another starts */
  doc.addEventListener(
    'play',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLVideoElement)) return;
      const activeRoot = target.closest('[data-bnn-video]');
      const activeWalkthrough = target.closest('[data-bnn-walkthrough]');

      doc.querySelectorAll('[data-bnn-video].is-playing').forEach((root) => {
        if (root !== activeRoot) pauseVideoRoot(root);
      });

      doc.querySelectorAll('[data-bnn-walkthrough]').forEach((gallery) => {
        if (gallery === activeWalkthrough) return;
        const walkVideo = gallery.querySelector('[data-bnn-walkthrough-video]');
        if (walkVideo && walkVideo !== target) {
          resetWalkthrough(gallery);
        }
      });
    },
    true
  );

  const wireWalkthrough = (gallery) => {
    if (!gallery || gallery.dataset.bnnWalkthroughWired === 'true') return;
    gallery.dataset.bnnWalkthroughWired = 'true';

    const video = gallery.querySelector('[data-bnn-walkthrough-video]');
    const source = gallery.querySelector('[data-bnn-walkthrough-source]');
    const play = gallery.querySelector('[data-bnn-walkthrough-play]');
    const poster = gallery.querySelector('[data-bnn-walkthrough-poster]');
    const posterImg = gallery.querySelector('[data-bnn-walkthrough-poster-img]');
    const fallback = gallery.querySelector('[data-bnn-walkthrough-fallback]');
    const labelEl = gallery.querySelector('[data-bnn-walkthrough-label]');
    const statusEl = gallery.querySelector('[data-bnn-walkthrough-status]');
    const player = gallery.querySelector('[data-bnn-walkthrough-player]');
    const clips = Array.from(gallery.querySelectorAll('[data-bnn-walkthrough-clip]'));

    if (!video || !source || !play) return;

    let activeSrc = gallery.getAttribute('data-bnn-initial-src') || '';
    let activePoster = gallery.getAttribute('data-bnn-initial-poster') || '';
    let activeLabel = gallery.getAttribute('data-bnn-initial-label') || '';
    let activeDesc = gallery.getAttribute('data-bnn-initial-desc') || activeLabel;

    const markPlaying = () => {
      if (player) player.classList.add('is-playing');
      play.hidden = true;
      if (poster) poster.hidden = true;
      if (fallback) fallback.hidden = true;
    };

    const markStopped = () => {
      if (player) player.classList.remove('is-playing');
      play.hidden = false;
      if (poster) poster.hidden = false;
    };

    const applyPoster = (posterUrl) => {
      if (posterUrl) {
        video.setAttribute('poster', posterUrl);
        if (poster) {
          poster.hidden = false;
          const shopifyImg = poster.querySelector('img:not([data-bnn-walkthrough-poster-img])');
          if (shopifyImg) shopifyImg.hidden = true;
          if (posterImg) {
            posterImg.hidden = false;
            posterImg.src = posterUrl;
          }
        }
      } else {
        video.removeAttribute('poster');
        if (poster) poster.hidden = true;
      }
    };

    const selectClip = (clipBtn, { announce = true } = {}) => {
      const nextSrc = clipBtn.getAttribute('data-bnn-clip-src') || '';
      const nextPoster = clipBtn.getAttribute('data-bnn-clip-poster') || '';
      const nextLabel = clipBtn.getAttribute('data-bnn-clip-label') || '';
      const nextDesc = clipBtn.getAttribute('data-bnn-clip-desc') || nextLabel;

      resetWalkthrough(gallery);

      activeSrc = nextSrc;
      activePoster = nextPoster;
      activeLabel = nextLabel;
      activeDesc = nextDesc;

      applyPoster(activePoster);
      video.setAttribute('aria-label', activeDesc || activeLabel || 'Project video');
      play.setAttribute('aria-label', `Play walkthrough: ${activeLabel || activeDesc || 'Walkthrough'}`);
      if (labelEl && activeLabel) labelEl.textContent = activeLabel;

      clips.forEach((btn) => {
        const active = btn === clipBtn;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      if (announce && statusEl && activeLabel) {
        statusEl.textContent = `Selected walkthrough: ${activeLabel}`;
      }
    };

    const ensureSource = () => {
      if (gallery.dataset.bnnWalkthroughReady === 'true') return true;
      if (!activeSrc) return false;
      source.setAttribute('src', activeSrc);
      video.load();
      gallery.dataset.bnnWalkthroughReady = 'true';
      return true;
    };

    applyPoster(activePoster);

    video.addEventListener('play', markPlaying);
    video.addEventListener('pause', () => {
      if (video.ended || video.currentTime < 0.05) markStopped();
    });
    video.addEventListener('ended', markStopped);
    video.addEventListener('error', () => {
      if (fallback) fallback.hidden = false;
      play.hidden = true;
      if (player) player.classList.remove('is-playing');
    });

    play.addEventListener('click', () => {
      if (!ensureSource()) {
        if (fallback) fallback.hidden = false;
        return;
      }
      video
        .play()
        .then(markPlaying)
        .catch(() => {
          if (fallback) fallback.hidden = false;
          play.hidden = true;
        });
    });

    clips.forEach((btn) => {
      btn.addEventListener('click', () => selectClip(btn));
    });
  };

  doc.querySelectorAll('[data-bnn-walkthrough]').forEach(wireWalkthrough);

  /* ——— Accessible gallery lightbox ——— */
  const findLightboxHolder = (trigger) => {
    if (!trigger) return null;
    const nested = trigger.querySelector('[data-bnn-lightbox-items]');
    if (nested) return nested;
    const card = trigger.closest('.bnn-concept-card');
    return card ? card.querySelector('[data-bnn-lightbox-items]') : null;
  };

  const readLightboxItems = (trigger) => {
    const holder = findLightboxHolder(trigger);
    if (holder) {
      const source = holder.content || holder;
      return Array.from(source.querySelectorAll('[data-bnn-lightbox-item]'))
        .map((el) => ({
          src: el.getAttribute('data-src') || '',
          srcset: el.getAttribute('data-srcset') || '',
          sizes: el.getAttribute('data-sizes') || '100vw',
          alt: el.getAttribute('data-alt') || '',
          width: el.getAttribute('data-width') || '',
          height: el.getAttribute('data-height') || ''
        }))
        .filter((item) => item.src);
    }

    const src = trigger.getAttribute('data-bnn-lightbox-src') || '';
    if (!src) return [];
    return [
      {
        src,
        srcset: '',
        sizes: '',
        alt: trigger.getAttribute('data-bnn-lightbox-alt') || '',
        width: '',
        height: ''
      }
    ];
  };

  const wireLightbox = (lightbox) => {
    if (!lightbox || lightbox.dataset.bnnLightboxWired === 'true') return;
    lightbox.dataset.bnnLightboxWired = 'true';

    const img = lightbox.querySelector('[data-bnn-lightbox-img]');
    const closeBtn = lightbox.querySelector('[data-bnn-lightbox-close]');
    const prevBtn = lightbox.querySelector('[data-bnn-lightbox-prev]');
    const nextBtn = lightbox.querySelector('[data-bnn-lightbox-next]');
    const countEl = lightbox.querySelector('[data-bnn-lightbox-count]');
    const statusEl = lightbox.querySelector('[data-bnn-lightbox-status]');
    let lastTrigger = null;
    let items = [];
    let index = 0;
    let loadToken = 0;
    let galleryMode = false;

    const isOpen = () => Boolean(lightbox.open);

    const clearImage = () => {
      if (!img) return;
      loadToken += 1;
      lightbox.classList.remove('is-ready');
      img.hidden = true;
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.alt = '';
      img.onload = null;
      img.onerror = null;
    };

    const setProtected = (on) => {
      if (!img) return;
      if (on) {
        lightbox.classList.add('bnn-lightbox--protect');
        img.setAttribute('data-bnn-protect-media', '');
        img.setAttribute('draggable', 'false');
      } else {
        lightbox.classList.remove('bnn-lightbox--protect');
        img.removeAttribute('data-bnn-protect-media');
        img.removeAttribute('draggable');
      }
    };

    const updateChrome = () => {
      const total = items.length;
      const multi = galleryMode && total > 1;
      if (prevBtn) prevBtn.hidden = !multi;
      if (nextBtn) nextBtn.hidden = !multi;
      if (countEl) {
        if (galleryMode && total > 0) {
          countEl.hidden = false;
          countEl.textContent = `Image ${index + 1} of ${total}`;
        } else {
          countEl.hidden = true;
          countEl.textContent = '';
        }
      }
    };

    const closeLightbox = () => {
      items = [];
      index = 0;
      galleryMode = false;
      clearImage();
      setProtected(false);
      updateChrome();
      if (statusEl) statusEl.textContent = '';
      if (typeof lightbox.close === 'function' && lightbox.open) {
        lightbox.close();
      } else {
        lightbox.removeAttribute('open');
      }
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus();
      }
      lastTrigger = null;
    };

    const revealImage = (token) => {
      if (token !== loadToken || !img) return;
      img.hidden = false;
      lightbox.classList.add('is-ready');
    };

    const showItem = (nextIndex) => {
      if (!img || !items.length) return;
      index = (nextIndex + items.length) % items.length;
      const item = items[index];
      clearImage();
      updateChrome();
      const token = loadToken;
      if (statusEl) {
        statusEl.textContent = galleryMode
          ? `Loading image ${index + 1} of ${items.length}`
          : 'Loading image';
      }

      if (!item || !item.src) {
        if (statusEl) statusEl.textContent = '';
        return;
      }

      const onReady = () => {
        revealImage(token);
        if (statusEl) {
          statusEl.textContent = galleryMode
            ? `Image ${index + 1} of ${items.length}`
            : 'Image';
        }
        img.onload = null;
        img.onerror = null;
      };

      img.onload = onReady;
      img.onerror = () => {
        if (token !== loadToken) return;
        if (!galleryMode || items.length <= 1) {
          closeLightbox();
          return;
        }
        onReady();
      };
      img.alt = item.alt || '';
      if (item.width) img.width = Number(item.width) || img.width;
      if (item.height) img.height = Number(item.height) || img.height;
      if (item.srcset) {
        img.srcset = item.srcset;
        img.sizes = item.sizes || '100vw';
      }
      img.src = item.src;

      if (img.complete && img.naturalWidth > 0) {
        onReady();
      }
    };

    const openLightbox = (trigger) => {
      if (!img) return;
      items = readLightboxItems(trigger);
      if (!items.length) return;

      lastTrigger = trigger;
      index = 0;
      galleryMode = trigger.hasAttribute('data-bnn-lightbox-gallery');
      setProtected(trigger.hasAttribute('data-bnn-lightbox-protect'));

      if (typeof lightbox.showModal === 'function') {
        lightbox.showModal();
      } else {
        lightbox.setAttribute('open', '');
      }

      showItem(0);
      if (closeBtn) closeBtn.focus();
    };

    const root = lightbox.closest('section') || doc;
    root.querySelectorAll('[data-bnn-lightbox-open]').forEach((trigger) => {
      if (trigger.dataset.bnnLightboxBound === 'true') return;
      trigger.dataset.bnnLightboxBound = 'true';
      trigger.addEventListener('click', () => openLightbox(trigger));
    });

    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (galleryMode && items.length > 1) showItem(index - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (galleryMode && items.length > 1) showItem(index + 1);
      });
    }

    lightbox.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeLightbox();
    });

    lightbox.addEventListener('close', () => {
      items = [];
      index = 0;
      galleryMode = false;
      clearImage();
      setProtected(false);
      updateChrome();
      if (statusEl) statusEl.textContent = '';
    });

    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) closeLightbox();
    });

    doc.addEventListener('keydown', (event) => {
      if (!isOpen()) return;
      if (event.key === 'Escape') {
        closeLightbox();
        return;
      }
      if (!galleryMode || items.length < 2) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showItem(index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showItem(index + 1);
      }
    });
  };

  doc.querySelectorAll('[data-bnn-lightbox]').forEach(wireLightbox);

  const isProtectedMedia = (target) =>
    Boolean(target && target.closest && target.closest('[data-bnn-protect-media]'));

  doc.addEventListener('contextmenu', (event) => {
    if (isProtectedMedia(event.target)) event.preventDefault();
  });
  doc.addEventListener('dragstart', (event) => {
    if (isProtectedMedia(event.target)) event.preventDefault();
  });

  /* ——— Contact inquiry context + submit protection ——— */
  const VALID_TIERS = new Set(['Design', 'Design Plus', 'Design + Build']);
  const MAX_CONTEXT_LEN = 200;

  const sanitizeRef = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (trimmed.includes('://') || trimmed.includes('//')) return '';
    if (/[<>"]/.test(trimmed)) return '';
    if (!(trimmed.startsWith('/') || trimmed.length <= 120)) return '';
    return trimmed.slice(0, MAX_CONTEXT_LEN);
  };

  const sanitizeProject = (value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim().slice(0, MAX_CONTEXT_LEN);
  };

  const wireContactInquiry = (root) => {
    if (!root || root.dataset.bnnContactWired === 'true') return;
    root.dataset.bnnContactWired = 'true';

    const form = root.querySelector('#BnnContactForm') || root.querySelector('form');
    if (!form) return;

    const tierSelect = form.querySelector('[data-bnn-service-tier]');
    const relatedField = form.querySelector('[data-bnn-related-project]');
    const referringField = form.querySelector('[data-bnn-referring-page]');
    const context = form.querySelector('[data-bnn-inquiry-context]');
    const contextTier = form.querySelector('[data-bnn-context-tier]');
    const contextTierValue = form.querySelector('[data-bnn-context-tier-value]');
    const contextProject = form.querySelector('[data-bnn-context-project]');
    const contextProjectValue = form.querySelector('[data-bnn-context-project-value]');
    const submit = form.querySelector('[data-bnn-submit]');
    const submitLabel = submit ? submit.querySelector('[data-bnn-submit-label]') : null;

    const updateContext = () => {
      if (!context) return;
      const tierValue = tierSelect && tierSelect.value ? tierSelect.value.trim() : '';
      const projectValue = relatedField ? sanitizeProject(relatedField.value) : '';
      const hasTier = Boolean(tierValue && VALID_TIERS.has(tierValue));
      const hasProject = Boolean(projectValue);
      let show = false;

      if (contextTier && contextTierValue) {
        if (hasTier) {
          contextTier.hidden = false;
          contextTier.removeAttribute('hidden');
          contextTierValue.textContent = tierValue;
          show = true;
        } else {
          contextTier.hidden = true;
          contextTier.setAttribute('hidden', '');
          contextTierValue.textContent = '';
        }
      }

      if (contextProject && contextProjectValue) {
        if (hasProject) {
          contextProject.hidden = false;
          contextProject.removeAttribute('hidden');
          contextProjectValue.textContent = projectValue;
          show = true;
        } else {
          contextProject.hidden = true;
          contextProject.setAttribute('hidden', '');
          contextProjectValue.textContent = '';
        }
      }

      if (show) {
        context.hidden = false;
        context.removeAttribute('hidden');
      } else {
        context.hidden = true;
        context.setAttribute('hidden', '');
      }
    };

    const applyQueryContext = () => {
      const params = new URLSearchParams(window.location.search || '');
      let tier = (params.get('tier') || '').trim();
      let project = sanitizeProject(params.get('project') || '');
      let referring = sanitizeRef(params.get('ref') || '');

      const postedTier = tierSelect
        ? (tierSelect.getAttribute('data-bnn-posted-tier') || '').trim()
        : '';

      if (tierSelect && !postedTier) {
        if (VALID_TIERS.has(tier)) {
          tierSelect.value = tier;
        }
      }

      if (relatedField && !relatedField.value && project) {
        relatedField.value = project;
      }
      if (referringField && !referringField.value && referring) {
        referringField.value = referring;
      }

      updateContext();
    };

    if (tierSelect) {
      tierSelect.addEventListener('change', updateContext);
    }

    applyQueryContext();

    const restoreSubmit = () => {
      if (!submit) return;
      submit.disabled = false;
      submit.removeAttribute('aria-disabled');
      submit.removeAttribute('aria-busy');
      if (submitLabel && submit.dataset.label) {
        submitLabel.textContent = submit.dataset.label;
      }
    };

    if (submit && submitLabel) {
      submit.dataset.label = submitLabel.textContent.trim();
    }

    form.addEventListener('submit', (event) => {
      if (!submit || submit.disabled) return;
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
        restoreSubmit();
        return;
      }
      submit.disabled = true;
      submit.setAttribute('aria-disabled', 'true');
      submit.setAttribute('aria-busy', 'true');
      if (submitLabel) {
        submitLabel.textContent =
          submit.getAttribute('data-submitting-label') || 'Sending…';
      }
    });

    form.addEventListener('invalid', restoreSubmit, true);

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) restoreSubmit();
    });

    restoreSubmit();
  };

  const revealInquiryStatus = () => {
    const status = doc.querySelector('[data-bnn-inquiry-status]');
    const root = doc.documentElement;
    const finishReturn = () => {
      root.classList.remove('bnn-inquiry-returning');
    };

    if (!status) {
      finishReturn();
      return;
    }
    if (status.dataset.bnnInquiryStatusHandled === 'true') {
      finishReturn();
      return;
    }
    status.dataset.bnnInquiryStatusHandled = 'true';

    try {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    } catch (_err) {
      /* ignore */
    }

    const previousInline = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';

    try {
      const headerEl = doc.querySelector('[data-bnn-header]');
      let offset = 96;
      if (headerEl) {
        const headerHeight = headerEl.getBoundingClientRect().height;
        if (headerHeight) offset = headerHeight + 16;
      }
      const top =
        status.getBoundingClientRect().top +
        (window.scrollY || window.pageYOffset || 0) -
        offset;
      window.scrollTo(0, Math.max(0, top));
      if (typeof status.focus === 'function') {
        status.focus({ preventScroll: true });
      }
    } finally {
      root.style.scrollBehavior = previousInline;
      finishReturn();
    }
  };

  doc.querySelectorAll('[data-bnn-contact-root]').forEach(wireContactInquiry);
  revealInquiryStatus();
})();
